import { forwardRef } from 'react';
import {
  Progress as ChakraProgress,
  type ProgressProps as ChakraProgressProps,
} from '@chakra-ui/react';

export interface ProgressProps extends ChakraProgressProps {
  /**
   * The value of the progress indicator.
   * If value is null, the progress bar will be in indeterminate state
   */
  value?: number;
  /**
   * The size of the progress bar
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * The color scheme of the progress bar
   * @default "primary"
   */
  colorScheme?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /**
   * If true, the progress bar will show a stripe animation
   * @default false
   */
  hasStripe?: boolean;
  /**
   * If true, the progress bar will animate
   * @default false
   */
  isAnimated?: boolean;
}

export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (props, ref) => {
    const { colorScheme = 'primary', size = 'md', ...rest } = props;

    const getHeight = () => {
      switch (size) {
        case 'sm':
          return '2px';
        case 'lg':
          return '8px';
        default:
          return '4px';
      }
    };

    const getColorScheme = () => {
      switch (colorScheme) {
        case 'primary':
          return 'brand';
        case 'success':
          return 'success';
        case 'warning':
          return 'warning';
        case 'danger':
          return 'danger';
        case 'info':
          return 'info';
        default:
          return 'brand';
      }
    };

    return (
      <ChakraProgress
        ref={ref}
        colorScheme={getColorScheme()}
        height={getHeight()}
        borderRadius="sm"
        bg="lineBorder"
        sx={{
          '& > div': {
            transition: 'all 0.2s',
            bg: getColorScheme(),
          },
        }}
        {...rest}
      />
    );
  }
);
